import { useEffect } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { Button } from '../../shared';
import styles from '../Main.module.scss';
import { emailKey, passwordKey, emailIsRequiredText, passwordIsRequiredText } from '../../shared/consts';
import { BeeheroGoogleSignIn } from './components/BeeheroGoogleSignIn';
import { continueText, loginText, orText } from './textConsts';
import useMediaQuery from '../../shared/hooks/useMediaQuery';
import { isMobileQuery } from '../../shared/MediaQuery/mediaQueryUtils';
import { useLogin } from './hooks/useLogin';
import { LoginFormFields } from './components/LoginFormFields';
import { invalidEmailText } from '../../shared/consts';
import { LoginErrorMessage } from './components/LoginErrorMessage';

const loginSchema = yup
    .object({
        [emailKey]: yup.string().email().required(emailIsRequiredText),
        [passwordKey]: yup.string().required(passwordIsRequiredText)
    })
    .required();

const LoginForm = ({
    appRedirection,
    setLoading,
    queryParams,
    inviteForm = false,
    email,
    token,
    props,
    displayGoogleSignIn = true
}) => {
    const isMobile = useMediaQuery(isMobileQuery);
    const { handleLogin, loginError, passwordResetUrl, navigateToPasswordReset } = useLogin(
        setLoading,
        queryParams,
        appRedirection
    );

    const {
        register,
        handleSubmit,
        setValue,
        control,
        formState: { errors }
    } = useForm({ resolver: yupResolver(loginSchema) });

    const onSubmit = (data) => {
        if (inviteForm) {
            if (data.email === email) {
                props.history.push(`/reset_password/${token}${queryParams}`);
            } else {
                setValue('formError', invalidEmailText);
            }
        } else {
            handleLogin(data);
        }
    };

    useEffect(() => {
        if (inviteForm) {
            setValue(emailKey, email);
            setValue(passwordKey, 'placeholderPassword');
        }
    }, [inviteForm, email, setValue]);

    return (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <LoginFormFields control={control} register={register} errors={errors} inviteForm={inviteForm} />

            <div className={styles.submitButton}>
                <Button wide color="yellow" type="submit">
                    {!inviteForm ? loginText : continueText}
                </Button>
            </div>

            <LoginErrorMessage
                error={loginError || errors.formError?.message}
                resetPasswordUrl={passwordResetUrl}
                onResetPasswordClick={navigateToPasswordReset}
            />

            {displayGoogleSignIn && !isMobile && (
                <>
                    <div className={styles.separatorContainer}>
                        <div className={styles.separator} />
                        {orText}
                        <div className={styles.separator} />
                    </div>
                    <BeeheroGoogleSignIn onSignIn={handleLogin} />
                </>
            )}
        </form>
    );
};

LoginForm.propTypes = {
    appRedirection: PropTypes.string,
    setLoading: PropTypes.func.isRequired,
    queryParams: PropTypes.string,
    inviteForm: PropTypes.bool,
    email: PropTypes.string,
    token: PropTypes.string,
    props: PropTypes.shape({
        history: PropTypes.shape({
            push: PropTypes.func.isRequired
        }).isRequired
    }).isRequired,
    displayGoogleSignIn: PropTypes.bool
};

export default LoginForm;
